import { useEffect, useReducer } from 'react';

export const PAUSE_COOLDOWN_MS = 1500;

/**
 * Returns true while a recording is younger than PAUSE_COOLDOWN_MS, signalling
 * that the pause button should be disabled. Anchored to recordingStartedAt,
 * which the transcription state machine resets on every entry into
 * PreparingRecording (initial start, RESUME, restart-after-failure, reconnect).
 *
 * Pass `undefined` when not currently in a recording state to disengage the
 * cooldown (e.g. while paused or idle).
 *
 * The returned boolean is derived from wall-clock time, not React state. The
 * forceUpdate below schedules exactly one re-render at the deadline so the
 * derived value flips to false at the right moment.
 */
export const useRecordingPauseCooldown = (
  recordingStartedAt: string | undefined,
): boolean => {
  const [, forceUpdate] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!recordingStartedAt) return;
    const remaining =
      PAUSE_COOLDOWN_MS - (Date.now() - new Date(recordingStartedAt).getTime());
    if (remaining <= 0) return;
    const id = setTimeout(forceUpdate, remaining);
    return () => clearTimeout(id);
  }, [recordingStartedAt]);

  if (!recordingStartedAt) return false;
  return (
    Date.now() - new Date(recordingStartedAt).getTime() < PAUSE_COOLDOWN_MS
  );
};
